import React, { useState } from 'react';
import { FaRobot, FaTrash, FaTimes, FaRedo, FaSearch } from 'react-icons/fa';
import { WaitingIndicator } from '@trip-planner-monorepo/shared';
import './AIResourcesModal.css';

/**
 * Modal for managing AI-generated resources with delete, reinstate, and generation capabilities
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal visibility state
 * @param {Function} props.onClose - Close handler
 * @param {string} props.placeName - Place name for modal title
 * @param {string} props.placeId - Place ID for resource operations
 * @param {Object} props.activeResources - Current active resources object (by category)
 * @param {Array} props.deletedResources - Array of deleted resources
 * @param {Function} props.onDelete - Handler to delete a resource (category, resourceIndex)
 * @param {Function} props.onReinstate - Handler to reinstate a resource (resource)
 * @param {Function} props.onGenerate - Handler to generate new resources
 * @param {boolean} props.isGenerating - Loading state for generation
 */
const AIResourcesModal = ({
  open,
  onClose,
  placeName,
  placeId,
  activeResources = {},
  deletedResources = [],
  onDelete,
  onReinstate,
  onGenerate,
  isGenerating = false
}) => {
  if (!open) return null;

  // Count total active resources
  const totalActiveResources = Object.values(activeResources).reduce(
    (sum, resources) => sum + (resources?.length || 0),
    0
  );

  // Category display names
  const categoryNames = {
    general: 'General Information',
    restaurants: 'Restaurants & Cafes',
    attractions: 'Attractions',
    transportation: 'Transportation',
    shopping: 'Shopping',
    nightlife: 'Nightlife & Entertainment'
  };

  // Define category display order (general always first)
  const categoryOrder = [
    'general',
    'restaurants',
    'attractions',
    'transportation',
    'shopping',
    'nightlife'
  ];

  // Sort categories according to defined order
  const sortedCategories = categoryOrder
    .map(category => [category, activeResources[category]])
    .filter(([_, resources]) => resources && resources.length > 0);

  return (
    <div className="ai-resources-modal-backdrop" onClick={onClose}>
      <div className="ai-resources-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="ai-resources-modal-header">
          <h2>
            <FaRobot className="modal-icon" />
            AI Resources for {placeName}
          </h2>
          <button className="modal-close-button" onClick={onClose} title="Close">
            <FaTimes />
          </button>
        </div>

        {/* Modal Content */}
        <div className="ai-resources-modal-content">
          {/* Section 1: Active Resources */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              Active Resources
              {totalActiveResources > 0 && (
                <span className="resource-count">({totalActiveResources})</span>
              )}
            </h3>
            {totalActiveResources > 0 ? (
              <div className="modal-resource-categories">
                {sortedCategories.map(([category, resources]) => (
                  <div key={category} className="modal-resource-category">
                    <h4 className="modal-category-title">
                      {categoryNames[category] || category}
                    </h4>
                    <div className="modal-resource-list">
                      {resources.map((resource, index) => (
                        <div key={resource.id || index} className={`modal-resource-item ${resource.isSearchFallback ? 'search-fallback' : ''}`}>
                          <a
                            href={resource.url}
                            className="modal-resource-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title={resource.description || resource.name}
                          >
                            <span className="modal-resource-name-text">{resource.name}</span>
                            {resource.isSearchFallback && (
                              <FaSearch className="search-badge" />
                            )}
                          </a>
                          <button
                            className="modal-delete-button"
                            onClick={() => onDelete(category, index)}
                            title="Delete this resource"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
                )}
              </div>
            ) : (
              <div className="modal-empty-state">
                <p>No active resources yet. Generate AI resources to get started.</p>
              </div>
            )}
          </div>

          {/* Section 2: Previously Deleted Resources */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              Previously Deleted Resources
              {deletedResources.length > 0 && (
                <span className="resource-count">({deletedResources.length})</span>
              )}
            </h3>
            {deletedResources.length > 0 ? (
              <div className="modal-resource-list">
                {deletedResources.map((resource, index) => (
                  <div key={index} className="modal-resource-item deleted">
                    <span className="modal-resource-name">{resource.name}</span>
                    <button
                      className="modal-reinstate-button"
                      onClick={() => onReinstate(resource)}
                      title="Reinstate this resource"
                    >
                      <FaRedo /> Reinstate
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="modal-empty-state">
                <p>No deleted resources.</p>
              </div>
            )}
          </div>

        </div>

        {/* Modal Footer (Sticky Actions) */}
        <div className="ai-resources-modal-footer">
          <div className="modal-section modal-actions">
            {isGenerating && (
              <div style={{ marginBottom: '20px', width: '100%' }}>
                <WaitingIndicator
                  size="medium"
                  message="Analyzing your trip context and finding relevant resources..."
                />
              </div>
            )}
            <button
              className="modal-generate-button"
              onClick={onGenerate}
              disabled={isGenerating}
            >
              <FaRobot />
              {isGenerating ? 'Generating...' : 'Generate New AI Resources'}
            </button>
            <p className="ai-disclaimer">
              (May take some time for AI to respond)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIResourcesModal;
